import React, { useState, useEffect } from 'react';
import {
  User,
  GraduationCap,
  Target,
  CreditCard,
  Download,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Bug,
  Calendar,
  Brain,
  BarChart3,
  MapPin,
  Users,
  Building,
  Award,
  TrendingUp,
  BookOpen,
  Clock,
  Star,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import {
  loadRazorpayScript,
  createRazorpayOrder,
  verifyPayment,
  validatePromoCode,
  debugRazorpayConnection,
  RAZORPAY_KEY_ID,
  type RazorpayResponse,
} from '../lib/razorpay';
import { personalizedListSubmissionService } from '../lib/database';
import {
  josaaService,
  type JoSAAFormData,
  type JoSAAPrediction,
} from '../lib/josaaService';
import { generateJoSAAPDF } from '../lib/josaaReportGenerator';

const basePrice = 299;

const steps = [
  {
    number: 1,
    title: 'Academic Details',
    description: 'Enter your JEE ranks and preferences',
  },
  {
    number: 2,
    title: 'Review & Payment',
    description: 'Preview number of predictions and complete payment',
  },
  {
    number: 3,
    title: 'Get Results',
    description: 'Download your personalized JoSAA predictions',
  },
];

const allStates = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Delhi',
  'Chandigarh',
  'Puducherry',
  'Andaman and Nicobar Islands',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
];

type PredictionChance = 'High' | 'Medium' | 'Low';

const JoSAAPersonalizedList: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [predictionCount, setPredictionCount] = useState(0);
  const [showDebug, setShowDebug] = useState(false);

  const [formData, setFormData] = useState<JoSAAFormData>({
    name: '',
    email: '',
    jeeMainCRL: '',
    jeeAdvancedQualified: false,
    jeeAdvancedRank: '',
    category: 'General',
    gender: 'Male',
    homeState: ''
  });

  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(basePrice);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const [predictions, setPredictions] = useState<JoSAAPrediction[]>([]);

  const [pdfGenerated, setPdfGenerated] = useState(false);

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.jeeMainCRL.trim()) {
      toast.error('Please enter your JEE Main CRL');
      return false;
    }
    const jeeMainRank = parseInt(formData.jeeMainCRL);
    if (isNaN(jeeMainRank) || jeeMainRank <= 0) {
      toast.error('Please enter a valid JEE Main CRL (positive number)');
      return false;
    }
    if (formData.jeeAdvancedQualified && !formData.jeeAdvancedRank.trim()) {
      toast.error('Please enter your JEE Advanced rank');
      return false;
    }
    if (formData.jeeAdvancedQualified) {
      const jeeAdvancedRank = parseInt(formData.jeeAdvancedRank);
      if (isNaN(jeeAdvancedRank) || jeeAdvancedRank <= 0) {
        toast.error('Please enter a valid JEE Advanced rank (positive number)');
        return false;
      }
    }
    if (!formData.homeState) {
      toast.error('Please select your home state');
      return false;
    }
    return true;
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    const result = await validatePromoCode(promoCode.trim(), 'personalized_list');
    if (result.isValid) {
      const applied = Math.min(result.discount, basePrice);
      setDiscount(applied);
      setFinalAmount(Math.max(0, basePrice - applied));
      toast.success(`🎉 Promo applied! ₹${applied} off`);
    } else {
      setDiscount(0);
      setFinalAmount(basePrice);
      toast.error('Invalid promo code');
    }
  };

  const removePromo = () => {
    setPromoCode('');
    setDiscount(0);
    setFinalAmount(basePrice);
    toast.success('Promo code removed');
  };

  const generatePreview = async () => {
    if (!validateStep1()) return;
    
    // Scroll to top when showing predictions
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setLoading(true);
    try {
      const preds = await josaaService.generatePredictions(formData);
      setPredictionCount(preds.length);
      setCurrentStep(2);
      toast.success(`Found ${preds.length} college predictions for you!`);
    } catch (err) {
      console.error('Preview error:', err);
      toast.error('Failed to generate preview. Please check your input data.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    setPaymentLoading(true);
    toast.loading('Initializing payment...', { id: 'payment' });

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway.', { id: 'payment' });
        setPaymentLoading(false);
        return;
      }

      // Free path: promo gives 100% discount, skip Razorpay entirely
      if (finalAmount === 0) {
        try {
          const sub = await personalizedListSubmissionService.create({
            name: formData.name,
            email: formData.email,
            exam: formData.jeeAdvancedQualified ? 'JEE Advanced' : 'JEE Main',
            rank: formData.jeeAdvancedQualified ? formData.jeeAdvancedRank : formData.jeeMainCRL,
            category: formData.category,
            states: [formData.homeState],
            stream: 'Engineering',
            amount_paid: 0,
            promo_code: promoCode || undefined,
            discount_amount: discount,
            payment_status: 'completed',
          });
          setSubmissionId(sub.id);
        } catch (dbErr: any) {
          toast.error('Failed to save submission. Try again.', { id: 'payment' });
          setPaymentLoading(false);
          return;
        }
        toast.success('Promo applied! Generating your predictions...', { id: 'payment' });
        await finalizeAfterPayment();
        return;
      }

      let order;
      try {
        order = await createRazorpayOrder(finalAmount);
        toast.success('Payment order created!', { id: 'payment' });
      } catch (orderErr: any) {
        console.error('Order creation failed:', orderErr);
        toast.error(`Order creation failed: ${orderErr.message}`, { id: 'payment' });
        setPaymentLoading(false);
        return;
      }

      let submission;
      try {
        submission = await personalizedListSubmissionService.create({
          name: formData.name,
          email: formData.email,
          exam: formData.jeeAdvancedQualified ? 'JEE Advanced' : 'JEE Main',
          rank: formData.jeeAdvancedQualified ? formData.jeeAdvancedRank : formData.jeeMainCRL,
          category: formData.category,
          states: [formData.homeState],
          stream: 'Engineering',
          amount_paid: finalAmount,
          promo_code: promoCode || undefined,
          discount_amount: discount,
          payment_status: 'pending',
          razorpay_order_id: order.id,
        });
        setSubmissionId(submission.id);
        toast.success('Submission recorded.', { id: 'payment' });
      } catch (dbErr: any) {
        console.error('DB error:', dbErr);
        toast.error('Failed to save submission. Try again.', { id: 'payment' });
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Grad Vikalp',
        description: 'JoSAA College Predictions',
        order_id: order.id,
        prefill: {
          name: formData.name,
          email: formData.email,
        },
        theme: { color: '#3b82f6' },
        handler: async (response: RazorpayResponse) => {
          try {
            toast.loading('Verifying payment...', { id: 'payment' });
            const verification = await verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              submission?.id || ''
            );
            if (verification.success) {
              toast.success('Payment verified! Generating full predictions...', { id: 'payment' });
              await finalizeAfterPayment();
            } else {
              toast.error('Payment verification failed', { id: 'payment' });
            }
          } catch (verErr) {
            console.error('Verification error:', verErr);
            toast.error('Verification error occurred', { id: 'payment' });
          }
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled', { id: 'payment' });
            setPaymentLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options as any);
      razorpay.open();
    } catch (err) {
      console.error('Payment flow error:', err);
      toast.error('Payment initiation failed', { id: 'payment' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const finalizeAfterPayment = async () => {
    setLoading(true);
    try {
      let preds = await josaaService.generatePredictions(formData);
      preds = preds
        .slice()
        .sort((a, b) => {
          const aRank = Number(a.cutoff.closing_rank);
          const bRank = Number(b.cutoff.closing_rank);
          return aRank - bRank;
        })
        .slice(0, 50);
      setPredictions(preds);
      await generateJoSAAPDF({
        studentInfo: formData,
        predictions: preds,
        paymentInfo: {
          amountPaid: finalAmount,
          promoCode: promoCode || null,
          discount: discount,
        },
      });
      setPdfGenerated(true);
      setCurrentStep(3);
      toast.success('JoSAA predictions ready and PDF generated!');
    } catch (err) {
      console.error('Finalization error:', err);
      toast.error('Failed to finalize after payment');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDFManually = async () => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      await generateJoSAAPDF({
        studentInfo: formData,
        predictions: predictions,
        paymentInfo: {
          amountPaid: finalAmount,
          promoCode: promoCode || null,
          discount: discount,
        },
      });
      toast.success('PDF downloaded successfully!', { id: 'pdf-download' });
    } catch (error) {
      console.error('Manual PDF download error:', error);
      toast.error('Failed to download PDF. Please try again.', { id: 'pdf-download' });
    }
  };

  const testPaymentSystem = async () => {
    toast.loading('Testing payment system...', { id: 'debug' });
    try {
      const result = await debugRazorpayConnection();
      if (result.success) {
        toast.success(result.message, { id: 'debug' });
      } else {
        toast.error(result.message, { id: 'debug' });
      }
    } catch (err) {
      console.error('Debug error:', err);
      toast.error('Debug test failed', { id: 'debug' });
    }
  };

  const resetAll = () => {
    setFormData({
      name: '',
      email: '',
      jeeMainCRL: '',
      jeeAdvancedQualified: false,
      jeeAdvancedRank: '',
      category: 'General',
      gender: 'Male',
      homeState: '',
      isPwD: false,
    });
    setPromoCode('');
    setDiscount(0);
    setFinalAmount(basePrice);
    setPredictions([]);
    setCurrentStep(1);
    setSubmissionId(null);
    setPredictionCount(0);
    setPdfGenerated(false);
  };

  const getAdmissionCounts = (preds: JoSAAPrediction[]) => {
    const high = preds.filter((p) => p.admissionChance === 'High');
    const medium = preds.filter((p) => p.admissionChance === 'Medium');
    const low = preds.filter((p) => p.admissionChance === 'Low');
    return { high, medium, low };
  };

  const { high, medium, low } = getAdmissionCounts(predictions);

  const getProbabilityBadgeClass = (chance: PredictionChance) =>
    chance === 'High' ? 'bg-success' : chance === 'Medium' ? 'bg-warning' : 'bg-danger';

  return (
    <div style={{ paddingTop: '70px' }} className="hero-gradient bg-light">
      <SEOHead
        title="JoSAA College Predictor - IIT, NIT, IIIT Admission Predictions | Grad Vikalp"
        description="Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks. AI-powered analysis with admission probability for just ₹299."
        keywords="JoSAA predictor, IIT admission predictor, NIT college predictor, JEE Advanced college list, JEE Main college predictor, IIT NIT IIIT admission, JoSAA counseling, college prediction tool"
        url="/josaa-personalized-list"
        type="website"
      />

      <div className="container py-3 py-md-5">
        {/* Header */}
        <div className="text-center mb-4 mb-md-5 px-2">
          <h1 className="display-4 fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1.75rem, 6vw, 3rem)' }}>
            JoSAA College Predictor
          </h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '700px', fontSize: 'clamp(1rem, 4vw, 1.25rem)' }}>
            Get accurate predictions for IIT, NIT, IIIT, and GFTI admissions based on your JEE Main and JEE Advanced ranks
          </p>
        </div>

        {/* Step Indicator */}
        <section className="row row-cols-1 row-cols-md-3 justify-content-center mb-2 mb-md-5 d-none d-md-flex">
          <div className="col-lg-10">
            <div className="d-flex justify-content-between align-items-start position-relative">
              {steps.map((step, idx) => (
                <div key={step.number} className="d-flex flex-column align-items-center text-center position-relative" style={{ flex: 1 }}>
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold mb-2 ${
                      currentStep > step.number
                        ? 'bg-success text-white'
                        : currentStep === step.number
                        ? 'bg-primary text-white'
                        : 'bg-light text-muted'
                    }`}
                    style={{ width: '50px', height: '50px' }}
                  >
                    {currentStep > step.number ? <CheckCircle size={24} /> : step.number}
                  </div>
                  <div className="small">
                    <div className="fw-semibold">{step.title}</div>
                    <div className="text-muted">{step.description}</div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className={`position-absolute`}
                      style={{
                        height: '4px',
                        background: currentStep > step.number ? '#198754' : '#d1d5db',
                        width: '100%',
                        top: '25px',
                        left: '50%',
                        transform: 'translateX(50%)',
                        zIndex: -1,
                      }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section> 

        {/* Step 1 */}
        {currentStep === 1 && (
          <div className="row g-5">
            <div className="col-lg-4">
              {/* Sidebar cards (omitted for brevity; same as previous implementation) */}
              <div className="vstack gap-4">
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h4 fw-bold text-dark mb-4">🎯 What You'll Get</h3>
                    <div className="vstack gap-3">
                      <div className="d-flex align-items-start gap-3">
                        <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 30, height: 30, fontSize: '12px' }}>
                          <Target size={16} />
                        </div>
                        <div>
                          <h6 className="fw-semibold text-dark mb-1">Dream Colleges</h6>
                          <p className="text-muted small mb-0">Top IITs and NITs you can aim for</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3">
                        <div className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 30, height: 30, fontSize: '12px' }}>
                          <CheckCircle size={16} />
                        </div>
                        <div>
                          <h6 className="fw-semibold text-dark mb-1">Safe Options</h6>
                          <p className="text-muted small mb-0">Colleges with high admission probability</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3">
                        <div className="bg-info rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 30, height: 30, fontSize: '12px' }}>
                          <BarChart3 size={16} />
                        </div>
                        <div>
                          <h6 className="fw-semibold text-dark mb-1">Detailed Analysis</h6>
                          <p className="text-muted small mb-0">Admission probability for each college</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3">
                        <div className="bg-warning rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 30, height: 30, fontSize: '12px' }}>
                          <Download size={16} />
                        </div>
                        <div>
                          <h6 className="fw-semibold text-dark mb-1">PDF Report</h6>
                          <p className="text-muted small mb-0">Downloadable detailed predictions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Pricing and Debug Tools */}
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h4 fw-bold text-dark mb-4">💰 Pricing</h3>
                    <div className="text-center">
                      <div className="display-6 fw-bold text-primary">₹{basePrice}</div>
                      <p className="text-muted">One-time payment</p>
                      <div className="alert alert-success">
                        <strong>🎉 Limited Time!</strong> Apply promo codes for discounts.
                      </div>
                      <div className="small text-muted">
                        <div>✓ Top 50 College Predictions</div>
                        <div>✓ Admission Probability Analysis</div>
                        <div>✓ Detailed PDF Report</div>
                        <div>✓ Choice Filling Strategy</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h3 className="h6 fw-bold text-dark mb-0">🔧 Debug Tools</h3>
                      <button
                        onClick={() => setShowDebug(!showDebug)}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        {showDebug ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {showDebug && (
                      <div className="vstack gap-2">
                        <button
                          onClick={testPaymentSystem}
                          className="btn btn-sm btn-outline-info d-flex align-items-center gap-2"
                        >
                          <Bug size={14} />
                          Test Payment System
                        </button>
                        <div className="small text-muted">
                          <div>Environment: {import.meta.env.MODE}</div>
                          <div>Supabase: {import.meta.env.VITE_SUPABASE_URL ? '✅' : '❌'}</div>
                          <div>Razorpay: {RAZORPAY_KEY_ID ? '✅' : '❌'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-xl">
                <div className="bg-blue-gradient text-white p-4">
                  <h2 className="h3 fw-bold mb-2">📋 Enter Your Academic Details</h2>
                  <p className="text-blue-100 mb-0">
                    Provide accurate information for precise college predictions
                  </p>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4">
                    {/* Personal Information */}
                    <div className="col-12">
                      <h4 className="h5 fw-bold text-dark mb-3">👤 Personal Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">Full Name *</label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter your full name"
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">Email Address *</label>
                          <input
                            type="email"
                            className="form-control form-control-lg"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">Gender</label>
                          <select
                            className="form-select form-select-lg"
                            value={formData.gender}
                            onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as 'Male' | 'Female' }))}
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">Home State *</label>
                          <select
                            className="form-select form-select-lg"
                            value={formData.homeState}
                            onChange={(e) => setFormData(prev => ({ ...prev, homeState: e.target.value }))}
                            required
                          >
                            <option value="">Select Your Home State</option>
                            {allStates.map((state) => (
                              <option key={state} value={state}>
                                {state}
                              </option>
                            ))}
                          </select>
                          <div className="form-text">
                            Important for home state quota calculations
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Academic Information */}
                    <div className="col-12">
                      <h4 className="h5 fw-bold text-dark mb-3">🎓 Academic Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">JEE Main CRL (Common Rank List) *</label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            value={formData.jeeMainCRL}
                            onChange={(e) => setFormData(prev => ({ ...prev, jeeMainCRL: e.target.value }))}
                            placeholder="Enter your JEE Main CRL"
                            required
                          />
                          <div className="form-text">
                            Your overall rank in JEE Main (e.g., 12345)
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark">Category</label>
                          <select
                            className="form-select form-select-lg"
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                          >
                            <option value="General">General</option>
                            <option value="OBC-NCL">OBC-NCL</option>
                            <option value="EWS">EWS</option>
                            <option value="SC">SC</option>
                            <option value="ST">ST</option>
                          </select>
                          <div className="form-text">
                            Your reservation category
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="card bg-light border-0">
                            <div className="card-body p-3">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  id="jeeAdvancedQualified"
                                  className="form-check-input"
                                  checked={formData.jeeAdvancedQualified}
                                  onChange={(e) => setFormData(prev => ({ ...prev, jeeAdvancedQualified: e.target.checked }))}
                                />
                                <label htmlFor="jeeAdvancedQualified" className="form-check-label fw-semibold">
                                  ✅ I have qualified JEE Advanced (Required for IIT admissions)
                                </label>
                              </div>
                              {formData.jeeAdvancedQualified && (
                                <div className="mt-3">
                                  <label className="form-label fw-semibold text-dark">JEE Advanced Rank *</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    value={formData.jeeAdvancedRank}
                                    onChange={(e) => setFormData(prev => ({ ...prev, jeeAdvancedRank: e.target.value }))}
                                    placeholder="Enter your JEE Advanced rank"
                                    required
                                  />
                                  <div className="form-text">
                                    Your rank in JEE Advanced (required for IIT predictions)
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              id="isPwD"
                              className="form-check-input"
                              checked={formData.isPwD}
                              onChange={(e) => setFormData(prev => ({ ...prev, isPwD: e.target.checked }))}
                            />
                            <label htmlFor="isPwD" className="form-check-label fw-semibold">
                              Person with Disability (PwD)
                            </label>
                          </div>
                          <div className="form-text">
                            Check if you have PwD certificate for additional quota
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Information Alert */}
                    <div className="col-12">
                      <div className="alert alert-info d-flex align-items-start gap-3">
                        <AlertCircle size={20} className="mt-1" />
                        <div>
                          <strong>Important:</strong> Ensure all information is accurate as it directly affects your college predictions. 
                          JEE Advanced qualification is mandatory for IIT predictions. Home state affects quota calculations.
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="col-12">
                      <div className="d-flex justify-content-end gap-3 mt-3">
                        <button
                          onClick={generatePreview}
                          disabled={loading}
                          className="btn btn-primary btn-lg d-flex align-items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                              Analyzing Your Rank...
                            </>
                          ) : (
                            <>
                              <Target size={20} />
                              Check My Predictions
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {currentStep === 2 && (
          <div className="row g-5">
            <div className="col-lg-8">
              <div className="card border-0 shadow-xl">
                <div className="bg-success text-white p-4">
                  <h2 className="h3 fw-bold mb-2">🎯 Your JoSAA Predictions Preview</h2>
                  <p className="text-light mb-0">
                    Based on your JEE {formData.jeeAdvancedQualified ? 'Advanced' : 'Main'} rank: {formData.jeeAdvancedQualified ? formData.jeeAdvancedRank : formData.jeeMainCRL}
                  </p>
                </div>
                <div className="card-body p-4">
                  <div className="alert alert-light border mb-4">
                    <h4 className="h5 fw-bold text-dark mb-3">📋 Your Profile Summary</h4>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Name:</span>
                          <span className="fw-medium">{formData.name}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Category:</span>
                          <span className="fw-medium">{formData.category}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Gender:</span>
                          <span className="fw-medium">{formData.gender}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Home State:</span>
                          <span className="fw-medium">{formData.homeState}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">JEE Main CRL:</span>
                          <span className="fw-medium">{formData.jeeMainCRL}</span>
                        </div>
                        {formData.jeeAdvancedQualified && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">JEE Advanced:</span>
                            <span className="fw-medium">{formData.jeeAdvancedRank}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <h4 className="h5 fw-bold text-dark mb-3">🔍 Prediction Summary</h4>
                  <div className="alert alert-info mb-4">
                    Based on your input, we can generate <strong>{predictionCount}</strong> college predictions for you. Complete payment to see the top 50 colleges (ascending order of closing cutoff rank) with full details and download your personalized PDF.
                  </div>

                  <div className="d-flex gap-3">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="btn btn-outline-secondary btn-lg"
                    >
                      ← Edit Details
                    </button>
                    <button
                      onClick={handlePayment}
                      className="btn btn-primary btn-lg flex-grow-1 d-flex align-items-center gap-2"
                      disabled={paymentLoading}
                    >
                      {paymentLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard size={20} />
                          Pay ₹{finalAmount} & Get Top 50 List →
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Sidebar */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-lg">
                <div className="card-header bg-primary text-white">
                  <h3 className="h5 mb-0">💳 Complete Payment</h3>
                </div>
                <div className="card-body p-4">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span>JoSAA Predictions</span>
                      <span className="fw-bold">₹{basePrice}</span>
                    </div>
                    {discount > 0 && (
                      <div className="d-flex justify-content-between text-success mb-2">
                        <span>Discount ({promoCode})</span>
                        <span className="fw-bold">-₹{discount}</span>
                      </div>
                    )}
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span className="fw-bold">Total Amount</span>
                      <span className="fw-bold h5 text-primary">₹{finalAmount}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">Promo Code</label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="form-control"
                        placeholder="Enter promo code"
                      />
                      {discount > 0 ? (
                        <button onClick={removePromo} className="btn btn-outline-danger">
                          Remove
                        </button>
                      ) : (
                        <button onClick={applyPromo} className="btn btn-outline-primary">
                          Apply
                        </button>
                      )}
                    </div>
                    <div className="form-text">
                      Try: NEET2025, WELCOME50, STUDENT100 for discounts
                    </div>
                  </div>

                  <button
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="btn btn-success btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                  >
                    {paymentLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={20} />
                        Pay ₹{finalAmount} & Get Top 50 List
                      </>
                    )}
                  </button>

                  <div className="text-center mt-3">
                    <div className="small text-muted">
                      🔒 Secure payment powered by Razorpay
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Results with richer list UI */}
        {currentStep === 3 && (
          <div className="row g-5">
            <div className="col-lg-10">
              <div className="card border-0 shadow-xl">
                <div className="bg-success text-white p-4 text-center">
                  <CheckCircle size={48} className="mb-3" />
                  <h2 className="h3 fw-bold mb-2">🎉 Your JoSAA Predictions Are Ready!</h2>
                  <p className="text-light mb-0">
                    {pdfGenerated ? 'PDF report has been generated and downloaded automatically' : 'Your predictions are ready'}
                  </p>
                </div>
                <div className="card-body p-5">
                  {/* Summary Stats */}
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <div className="d-flex gap-3">
                      <div className="card bg-success bg-opacity-10 border-0">
                        <div className="card-body p-3 text-center">
                          <h4 className="fw-bold text-success mb-1">{high.length}</h4>
                          <div className="small text-muted">High Chance</div>
                        </div>
                      </div>
                      <div className="card bg-warning bg-opacity-10 border-0">
                        <div className="card-body p-3 text-center">
                          <h4 className="fw-bold text-warning mb-1">{medium.length}</h4>
                          <div className="small text-muted">Medium Chance</div>
                        </div>
                      </div>
                      <div className="card bg-danger bg-opacity-10 border-0">
                        <div className="card-body p-3 text-center">
                          <h4 className="fw-bold text-danger mb-1">{low.length}</h4>
                          <div className="small text-muted">Low Chance</div>
                        </div>
                      </div>
                      <div className="card bg-primary bg-opacity-10 border-0">
                        <div className="card-body p-3 text-center">
                          <h4 className="fw-bold text-primary mb-1">{predictions.length}</h4>
                          <div className="small text-muted">Total Predictions</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="d-flex gap-2">
                        <button
                          onClick={downloadPDFManually}
                          className="btn btn-success btn-lg d-flex align-items-center gap-2"
                        >
                          <Download size={18} />
                          Download PDF Report
                        </button>
                        <button
                          onClick={resetAll}
                          className="btn btn-outline-primary btn-lg"
                        >
                          Generate New List
                        </button>
                        <Link to="/contact" className="btn btn-success btn-lg">
                          Get Expert Counseling
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Top 50 List */}
                  <h3 className="h4 fw-bold text-dark mb-4">🏆 Your Top 50 College Predictions</h3>
                  <div className="row g-4">
                    {predictions.map((prediction, index) => (
                      <div key={index} className="col-lg-6">
                        <div className="card border h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span className="badge bg-primary">{index + 1}</span>
                                  <h5 className="fw-bold text-dark mb-0">{prediction.institute.name}</h5>
                                  <span className="badge bg-secondary">{prediction.institute.institute_type}</span>
                                </div>
                                <p className="text-primary mb-2">
                                  {prediction.branch.branch_name} ({prediction.branch.branch_code})
                                </p>
                              </div>
                              <div className="text-end">
                                <span className={`badge fs-6 px-3 py-2 ${getProbabilityBadgeClass(prediction.admissionChance as PredictionChance)}`}>
                                  {prediction.probabilityPercentage}%
                                </span>
                                <div className="small text-muted mt-1">
                                  {prediction.admissionChance}
                                </div>
                              </div>
                            </div>
                            <div className="row g-2 small text-muted mb-2">
                              <div className="col-md-6 d-flex align-items-center gap-1">
                                <MapPin size={12} />
                                <span>{prediction.institute.location}, {prediction.institute.state}</span>
                              </div>
                              <div className="col-md-6 d-flex align-items-center gap-1">
                                <Users size={12} />
                                <span>Closing Rank: {prediction.cutoff.closing_rank}</span>
                              </div>
                              <div className="col-md-6 d-flex align-items-center gap-1">
                                <Calendar size={12} />
                                <span>{prediction.cutoff.round_name} ({prediction.cutoff.year})</span>
                              </div>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => {
                                  if (prediction.institute.website) {
                                    window.open(prediction.institute.website, '_blank', 'noopener,noreferrer');
                                  } else {
                                    toast.error('Institution website not available');
                                  }
                                }}
                                className={`btn btn-outline-info btn-sm ${!prediction.institute.website ? 'disabled' : ''}`}
                                disabled={!prediction.institute.website}
                              >
                                <ExternalLink size={14} className="me-1" />
                                View Institution Details
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {predictions.length === 0 && (
                    <div className="text-center py-5">
                      <GraduationCap size={64} className="text-muted mb-3" />
                      <h4 className="text-muted">No predictions available</h4>
                      <p className="text-muted">
                        Something went wrong. Try generating a new list.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Side success indicators */}
            <div className="col-lg-2">
              <div className="vstack gap-3">
                <div className="card bg-light border-0">
                  <div className="card-body p-3 text-center">
                    <Download size={32} className="text-primary mb-2" />
                    <h6 className="fw-bold text-dark mb-2">PDF Downloaded</h6>
                    <p className="small text-muted mb-0">
                      {pdfGenerated ? 'Your detailed report has been downloaded automatically' : 'Click the download button to get your PDF'}
                    </p>
                    {!pdfGenerated && (
                      <button
                        onClick={downloadPDFManually}
                        className="btn btn-primary btn-sm mt-2 d-flex align-items-center gap-1 mx-auto"
                      >
                        <Download size={14} />
                        Download Now
                      </button>
                    )}
                  </div>
                </div>
                <div className="card bg-light border-0">
                  <div className="card-body p-3 text-center">
                    <CheckCircle size={32} className="text-success mb-2" />
                    <h6 className="fw-bold text-dark mb-2">Payment Verified</h6>
                    <p className="small text-muted mb-0">
                      Your payment has been successfully processed
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ & CTA (can remain as before) */}
        <div className="mt-5">
          <div className="card bg-light border-0">
            <div className="card-body p-5">
              <h2 className="h3 fw-bold text-dark text-center mb-5">❓ Frequently Asked Questions</h2>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card border-0 bg-white h-100">
                    <div className="card-body p-4">
                      <h4 className="h5 fw-bold text-dark mb-3">How accurate are the predictions?</h4>
                      <p className="text-muted mb-0">
                        Our predictions are based on historical JoSAA data and use advanced algorithms to calculate admission probabilities. 
                        While highly accurate, actual results may vary based on current year competition.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-0 bg-white h-100">
                    <div className="card-body p-4">
                      <h4 className="h5 fw-bold text-dark mb-3">Do I need JEE Advanced for all predictions?</h4>
                      <p className="text-muted mb-0">
                        JEE Advanced is only required for IIT predictions. For NITs, IIITs, and GFTIs, 
                        your JEE Main rank is sufficient.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-0 bg-white h-100">
                    <div className="card-body p-4">
                      <h4 className="h5 fw-bold text-dark mb-3">What's included in the PDF report?</h4>
                      <p className="text-muted mb-0">
                        Complete college list with admission probabilities, choice filling strategy, round-wise analysis, and detailed institute information.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-0 bg-white h-100">
                    <div className="card-body p-4">
                      <h4 className="h5 fw-bold text-dark mb-3">Can I get a refund?</h4>
                      <p className="text-muted mb-0">
                        Refunds are available within 24 hours if there are technical issues. 
                        Please check our refund policy for complete details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-5">
          <div className="card bg-blue-gradient border-0 text-white">
            <div className="card-body p-5 text-center">
              <h2 className="h3 fw-bold mb-3">🎯 Ready to Find Your Perfect College?</h2>
              <p className="text-blue-100 mb-4">
                Join thousands of students who have successfully used our JoSAA predictor to secure admissions in top institutes
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                {currentStep === 1 && (
                  <button
                    onClick={generatePreview}
                    className="btn btn-light text-primary btn-lg fw-semibold"
                  >
                    Start Your Prediction Now
                  </button>
                )}
                <Link to="/contact" className="btn btn-outline-light btn-lg">
                  Get Expert Guidance
                </Link>
              </div>
            </div>
          </div>
        </div>

              {/* What You'll Get Section - Reduced top margin for mobile */}
              <section className="mt-2">
                <div className="card border-0 shadow-lg">
                  <div className="card-header bg-info text-white">
                    <h3 className="h5 fw-bold mb-0">🎯 What You'll Get in Your JoSAA Report</h3>
                  </div>
                  <div className="card-body p-4">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <Target size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Top 50 College Predictions</h6>
                            <p className="text-muted small mb-0">
                              Carefully curated list of colleges based on your rank, sorted by admission probability
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <BarChart3 size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Admission Probability Analysis</h6>
                            <p className="text-muted small mb-0">
                              High, Medium, Low probability categorization with percentage chances
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-info rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <Brain size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Choice Filling Strategy</h6>
                            <p className="text-muted small mb-0">
                              Expert guidance on how to fill your JoSAA choices for maximum success
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-warning rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <Download size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Detailed PDF Report</h6>
                            <p className="text-muted small mb-0">
                              Comprehensive report with college details, cutoffs, and admission insights
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-danger rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <TrendingUp size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Historical Trend Analysis</h6>
                            <p className="text-muted small mb-0">
                              5+ years of cutoff data analysis to predict future trends
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className="bg-secondary rounded-circle d-inline-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40 }}>
                            <Award size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold text-dark mb-2">Institute Rankings & Details</h6>
                            <p className="text-muted small mb-0">
                              NIRF rankings, placement statistics, and comprehensive institute information
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
      </div>

      {/* SEO-Friendly Content Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-8">
              <div className="card border-0 shadow-lg">
                <div className="card-body p-5">
                  <h2 className="h1 fw-bold text-dark mb-4">
                    Complete Guide to JoSAA College Prediction 2025
                  </h2>
                  
                  <div className="blog-content">
                    <p className="lead text-muted mb-4">
                      Get accurate JoSAA college predictions based on your JEE Main and JEE Advanced ranks. Our AI-powered predictor analyzes historical cutoff data from IITs, NITs, IIITs, and GFTIs to provide you with the most reliable admission probability analysis.
                    </p>

                    <h3>How Our JoSAA Predictor Works</h3>
                    <p>
                      Our JoSAA college predictor uses advanced algorithms to analyze your JEE Main CRL (Common Rank List) and JEE Advanced rank against historical cutoff data from previous years. The system considers multiple factors including:
                    </p>
                    <ul>
                      <li><strong>Your JEE Main CRL:</strong> Used for NIT, IIIT, and GFTI predictions</li>
                      <li><strong>JEE Advanced Rank:</strong> Essential for IIT admission predictions</li>
                      <li><strong>Category:</strong> General, OBC-NCL, EWS, SC, ST reservations</li>
                      <li><strong>Gender:</strong> Female-only seats and supernumerary quotas</li>
                      <li><strong>Home State:</strong> State quota benefits for NITs</li>
                      <li><strong>Historical Trends:</strong> 5+ years of cutoff analysis</li>
                    </ul>

                    <h3>JoSAA 2025: What's New</h3>
                    <p>
                      The Joint Seat Allocation Authority (JoSAA) conducts centralized counseling for admission to IITs, NITs, IIITs, and other Government Funded Technical Institutes (GFTIs). For 2025, several important updates include:
                    </p>
                    <ul>
                      <li>Updated seat matrix with new programs and increased capacity</li>
                      <li>Revised reservation policies and implementation</li>
                      <li>Enhanced online counseling process</li>
                      <li>New institutes added to the JoSAA counseling process</li>
                    </ul>

                    <h3>Understanding JoSAA Cutoffs and Ranks</h3>
                    <p>
                      JoSAA cutoffs vary significantly based on multiple factors. Our predictor helps you understand:
                    </p>
                    
                    <table className="table table-bordered table-hover">
                      <thead className="table-primary">
                        <tr>
                          <th>Institute Type</th>
                          <th>Exam Required</th>
                          <th>Typical Cutoff Range</th>
                          <th>Total Seats</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>IIT</strong></td>
                          <td>JEE Advanced</td>
                          <td>1 - 15,000</td>
                          <td>16,000+</td>
                        </tr>
                        <tr>
                          <td><strong>NIT</strong></td>
                          <td>JEE Main</td>
                          <td>1,000 - 50,000</td>
                          <td>23,000+</td>
                        </tr>
                        <tr>
                          <td><strong>IIIT</strong></td>
                          <td>JEE Main</td>
                          <td>2,000 - 30,000</td>
                          <td>4,000+</td>
                        </tr>
                        <tr>
                          <td><strong>GFTI</strong></td>
                          <td>JEE Main</td>
                          <td>5,000 - 80,000</td>
                          <td>10,000+</td>
                        </tr>
                      </tbody>
                    </table>

                    <h3>JoSAA Choice Filling Strategy</h3>
                    <p>
                      Successful JoSAA counseling requires a strategic approach to choice filling. Our predictor categorizes colleges into three types to help you make informed decisions:
                    </p>
                    
                    <blockquote>
                      <p><strong>Dream Colleges:</strong> Institutes where your rank is close to the cutoff. These represent your aspirational choices with moderate admission chances.</p>
                    </blockquote>
                    
                    <blockquote>
                      <p><strong>Target Colleges:</strong> Institutes where you have a good probability of admission based on your rank and category.</p>
                    </blockquote>
                    
                    <blockquote>
                      <p><strong>Safety Colleges:</strong> Institutes where your admission is highly probable, ensuring you secure a seat in the counseling process.</p>
                    </blockquote>

                    <h3>Benefits of Using Our JoSAA Predictor</h3>
                    <ul>
                      <li><strong>Accurate Predictions:</strong> Based on 5+ years of historical JoSAA cutoff data</li>
                      <li><strong>Comprehensive Analysis:</strong> Covers all IITs, NITs, IIITs, and GFTIs</li>
                      <li><strong>Category-wise Cutoffs:</strong> Separate analysis for General, OBC, SC, ST, EWS</li>
                      <li><strong>State Quota Benefits:</strong> Considers home state advantages for NITs</li>
                      <li><strong>Gender-based Seats:</strong> Includes female-only and supernumerary quotas</li>
                      <li><strong>Round-wise Analysis:</strong> Shows cutoff trends across different counseling rounds</li>
                      <li><strong>Probability Percentage:</strong> Clear admission probability for each college-branch combination</li>
                    </ul>

                    <h3>JoSAA 2025 Important Dates</h3>
                    <p>
                      Stay updated with the JoSAA 2025 counseling schedule:
                    </p>
                    <ul>
                      <li><strong>Choice Filling:</strong> Multiple rounds from June to July</li>
                      <li><strong>Seat Allocation:</strong> Results announced round-wise</li>
                      <li><strong>Document Verification:</strong> At allocated institutes</li>
                      <li><strong>Fee Payment:</strong> To confirm seat acceptance</li>
                    </ul>

                    <h3>Frequently Asked Questions</h3>
                    <p>
                      Our JoSAA predictor addresses common questions about college admission predictions, cutoff analysis, and choice filling strategies. Get expert guidance on maximizing your chances of securing admission to your preferred engineering college.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="vstack gap-4">
                {/* Quick Stats */}
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold text-dark mb-3">JoSAA 2025 Statistics</h3>
                    <div className="vstack gap-3">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total IITs:</span>
                        <span className="fw-bold text-primary">23</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total NITs:</span>
                        <span className="fw-bold text-success">31</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total IIITs:</span>
                        <span className="fw-bold text-info">25</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total GFTIs:</span>
                        <span className="fw-bold text-warning">33</span>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total Seats:</span>
                        <span className="fw-bold text-dark">50,000+</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Popular Branches */}
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold text-dark mb-3">Popular Engineering Branches</h3>
                    <div className="vstack gap-2">
                      <span className="badge bg-primary bg-opacity-10 text-primary p-2">Computer Science Engineering</span>
                      <span className="badge bg-success bg-opacity-10 text-success p-2">Electronics & Communication</span>
                      <span className="badge bg-info bg-opacity-10 text-info p-2">Mechanical Engineering</span>
                      <span className="badge bg-warning bg-opacity-10 text-warning p-2">Electrical Engineering</span>
                      <span className="badge bg-danger bg-opacity-10 text-danger p-2">Civil Engineering</span>
                      <span className="badge bg-secondary bg-opacity-10 text-secondary p-2">Chemical Engineering</span>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold text-dark mb-3">💡 Pro Tips</h3>
                    <div className="vstack gap-3 small">
                      <div className="d-flex align-items-start gap-2">
                        <span className="badge bg-primary rounded-circle">1</span>
                        <span>Fill all 200+ choices available in JoSAA</span>
                      </div>
                      <div className="d-flex align-items-start gap-2">
                        <span className="badge bg-success rounded-circle">2</span>
                        <span>Prioritize branch over institute ranking</span>
                      </div>
                      <div className="d-flex align-items-start gap-2">
                        <span className="badge bg-info rounded-circle">3</span>
                        <span>Include safety options to secure a seat</span>
                      </div>
                      <div className="d-flex align-items-start gap-2">
                        <span className="badge bg-warning rounded-circle">4</span>
                        <span>Consider location and cost factors</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> 
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoSAAPersonalizedList;